import { useState } from 'react';
import { Heart, Shield, AlertTriangle, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAgeVerification } from '@/hooks/useAgeVerification';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';
import AdPanel from '@/components/ads/AdPanel';

const AgeVerification = () => {
  const { verifyAge, error: hookError } = useAgeVerification();
  const navigate = useNavigate();
  const [showExitMessage, setShowExitMessage] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);

  const handleYes = async () => {
    console.log('=== BUTTON CLICKED: Yes, I am 18 or older ===');
    
    setIsVerifying(true);
    setVerificationError(null);
    setShowSuccess(false);
    
    try {
      console.log('Calling verifyAge');
      
      const result = await verifyAge();
      console.log('Verification completed with result:', result);
      
      if (result.success) {
        console.log('✅ Verification successful, showing success state');
        setShowSuccess(true);
        
        toast({
          title: "Age Verified! ✅",
          description: "Redirecting you to sign up or log in...",
        });
        
        // Redirect to auth page where Google OAuth is available
        setTimeout(() => {
          const targetRoute = result.targetRoute || '/auth';
          console.log(`🚀 Navigating to: ${targetRoute}`);
          navigate(targetRoute, { replace: true });
        }, 1000);
      } else {
        console.log('❌ Verification failed:', result.error);
        setVerificationError(result.error || 'Verification failed. Please try again.');
        toast({
          title: "Verification Issue",
          description: result.error || "Please try again or refresh the page.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('❌ Unexpected error during verification:', error);
      setVerificationError('An unexpected error occurred. Please refresh the page and try again.');
      toast({
        title: "Error",
        description: "Please refresh the page and try again.",
        variant: "destructive"
      });
    } finally {
      if (!showSuccess) {
        setIsVerifying(false);
      }
    }
  };

  const handleNo = () => {
    console.log('=== BUTTON CLICKED: No, I am under 18 ===');
    setShowExitMessage(true);
  };

  const handleExit = () => {
    console.log('=== BUTTON CLICKED: Leave Site ===');
    window.location.href = 'https://www.ardmediathek.de/sendung/unser-sandmaennchen/Y3JpZDovL3JiYi1vbmxpbmUuZGUvc2FuZG1hbm4?isChildContent';
  };

  const handleRetry = () => {
    console.log('=== BUTTON CLICKED: Retry ===');
    setVerificationError(null);
    setIsVerifying(false);
    setShowSuccess(false);
  };

  const handleRefreshPage = () => {
    console.log('=== BUTTON CLICKED: Refresh Page ===');
    window.location.reload();
  };

  if (showExitMessage) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#121212' }}>
        <AdPanel />
        <div className="flex items-center justify-center p-4" style={{ minHeight: 'calc(100vh - calc(100vh / 7))' }}>
          <Card className="w-full max-w-md mx-auto border-neon-red/20 glass-card">
            <CardHeader className="text-center">
              <AlertTriangle className="w-16 h-16 text-neon-red mx-auto mb-4 glow-neon-red" />
              <CardTitle className="text-2xl heading text-ice-white">Access Restricted</CardTitle>
              <CardDescription className="body-text text-ice-white/70">
                You must be 18 or older to access this site. You will be redirected to a safe website.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={handleExit} 
                className="w-full bg-neon-red hover:bg-neon-red/80 glow-neon-red font-manrope text-ice-white"
                size="lg"
              >
                Leave Site
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative bg-gradient-to-br from-carbon via-carbon to-electric-violet/20">
      {/* Dark overlay for readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-carbon/80 via-transparent to-carbon/60 z-0"></div>
      
      <AdPanel />
      
      <div className="relative z-10 flex items-center justify-center p-4" style={{ minHeight: 'calc(100vh - calc(100vh / 7))' }}>
        <div className="w-full max-w-lg mx-auto p-6">
          <div className="text-center mb-6">
            <h1 className="text-2xl mb-2 heading text-ice-white">Age Verification Required</h1>
            <p className="text-lg body-text text-ice-white/70">
              You must be 18 years or older to enter this dating platform
            </p>
          </div>
          
          <div className="space-y-6">
            {showSuccess && (
              <div className="rounded-lg p-4 border-toxic-green/20 glass" style={{ borderColor: 'rgba(0, 255, 157, 0.2)' }}>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-toxic-green flex-shrink-0 mt-0.5 glow-toxic-green" />
                  <div className="text-sm text-ice-white">
                    <p className="font-manrope font-medium mb-1">Verification Successful!</p>
                    <p className="body-text">Age verified successfully. Redirecting you to sign up or log in...</p>
                  </div>
                </div>
              </div>
            )}

            {(hookError || verificationError) && !showSuccess && (
              <div className="rounded-lg p-4 border-neon-red/20 glass" style={{ borderColor: 'rgba(225, 6, 0, 0.2)' }}>
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-6 h-6 text-neon-red flex-shrink-0 mt-0.5 glow-neon-red" />
                  <div className="text-sm text-ice-white">
                    <p className="font-manrope font-medium mb-1">Verification Issue</p>
                    <p className="body-text">{hookError || verificationError}</p>
                    <div className="flex gap-2 mt-2">
                      <Button 
                        onClick={handleRetry} 
                        variant="outline" 
                        size="sm"
                        disabled={isVerifying}
                        className="border-ice-white/20 hover:bg-ice-white/10 font-manrope text-ice-white"
                      >
                        Try Again
                      </Button>
                      <Button 
                        onClick={handleRefreshPage} 
                        variant="outline" 
                        size="sm"
                        disabled={isVerifying}
                        className="border-ice-white/20 hover:bg-ice-white/10 font-manrope text-ice-white"
                      >
                        Refresh Page
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!showSuccess && (
              <>
                <div className="rounded-lg p-4 border-neon-red/20 glass" style={{ borderColor: 'rgba(225, 6, 0, 0.2)' }}>
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="w-6 h-6 text-neon-red flex-shrink-0 mt-0.5 glow-neon-red" />
                    <div className="text-sm text-ice-white">
                      <p className="font-manrope font-medium mb-1">Adult Content Warning</p>
                      <p className="body-text">
                        This website contains adult content and is intended for users 18 years and older. 
                        By entering, you confirm that you are of legal age in your jurisdiction.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg p-4 border-electric-violet/20 glass" style={{ borderColor: 'rgba(168, 0, 255, 0.2)' }}>
                  <div className="flex items-start space-x-3">
                    <Shield className="w-6 h-6 text-electric-violet flex-shrink-0 mt-0.5 glow-electric-violet" />
                    <div className="text-sm text-ice-white">
                      <p className="font-manrope font-medium mb-1">Privacy & Safety</p>
                      <p className="body-text">
                        We are committed to providing a safe environment and protecting user privacy. 
                        Your age verification helps us comply with legal requirements.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg p-4 border-toxic-green/20 glass" style={{ borderColor: 'rgba(0, 255, 157, 0.2)' }}>
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="w-6 h-6 text-toxic-green flex-shrink-0 mt-0.5 glow-toxic-green" />
                    <div className="text-sm text-ice-white">
                      <p className="font-manrope font-medium mb-1">Private Browsing Notice</p>
                      <p className="body-text">
                        If you're using incognito/private mode, your verification may not persist between page refreshes.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="text-center">
                  <p className="text-lg font-manrope font-medium mb-6 text-ice-white">
                    Are you 18 years of age or older?
                  </p>
                  
                  <div className="space-y-3">
                    <Button 
                      onClick={handleYes} 
                      disabled={isVerifying}
                      className="w-full bg-neon-red hover:bg-neon-red/80 disabled:opacity-50 glow-neon-red font-manrope text-ice-white"
                      size="lg"
                    >
                      {isVerifying ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        'Yes, I am 18 or older'
                      )}
                    </Button>
                    
                    <Button 
                      onClick={handleNo} 
                      disabled={isVerifying}
                      variant="outline" 
                      className="w-full border-ice-white/20 hover:bg-ice-white/10 disabled:opacity-50 font-manrope text-ice-white"
                      size="lg"
                    >
                      No, I am under 18
                    </Button>
                  </div>
                </div>

                <div className="text-xs text-center space-y-1 body-text text-ice-white/50">
                  <p>
                    By clicking "Yes, I am 18 or older", you confirm that you have read and agree to our 
                    Terms of Service and Privacy Policy.
                  </p>
                  <p>
                    This verification will be remembered for 1 day on this device (if storage is available).
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgeVerification;
